import React, { useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { getDayName, formatTime12Hour, checkTimeSlotConflicts } from '../../../utils/schedulingUtils';
import {
	fetchAllTimeSlots,
	addTimeSlot,
	updateTimeSlot,
	deleteTimeSlot,
	deleteTimeSlots,
	updateTimeSlotsStatus,
	confirmHoldPatch,
	releaseHoldPatch,
	unassignSlotPatch,
	confirmProvisionalHolds,
	releaseProvisionalHolds,
} from '../../../services/timeSlotService';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import AccessNotice from '../../../components/Portal/AccessNotice';
import PropertyPicker from '../../../components/Portal/PropertyPicker';
import { subscribeToMyPrefs, saveMyPrefs } from '../../../services/UserPrefsService';
import { isPermissionDenied } from '../../../utils/permissions';
import {
	SLOT_COLUMNS,
	DEFAULT_SLOT_COLUMNS,
	DEFAULT_SLOT_SORT,
	normalizeSlotColumns,
	hiddenColumnClasses,
	sortSlots,
	nextSort,
} from './slotColumns';
import {
	slotKind,
	slotWeekday,
	formatSlotDate,
	isPastDate,
	isOpenForRegistration,
	isOpenForMakeup,
} from './slotKind';

const DAYS_OF_WEEK = [
	{ value: 1, label: 'Monday' },
	{ value: 2, label: 'Tuesday' },
	{ value: 3, label: 'Wednesday' },
	{ value: 4, label: 'Thursday' },
	{ value: 5, label: 'Friday' },
	{ value: 6, label: 'Saturday' },
	{ value: 0, label: 'Sunday' }
];

const INSTRUCTORS = ['Grace', 'Anthony', 'Annelise'];
const LOCATIONS = ['Lakeville', 'HLDC'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// The Day cell says WHICH kind of slot a row is, in words, on every row and at
// every width (the Day column cannot be hidden): "Every Friday" for a weekly
// time, the date plus "Makeup, this date only" for a one-day slot. Blocking the
// second closes one lesson; blocking the first takes a weekly time out of
// /register, and the table used to render them identically (slotKind.js).
const SlotDayCell = ({ slot }) => {
	const kind = slotKind(slot);
	if (kind === 'weekly') {
		return (
			<span className="slot-editor__day">
				<span className="ps-cell-strong">{getDayName(slot.dayOfWeek)}</span>
				<span className="slot-editor__kind">
					<i className="fa-sharp fa-solid fa-repeat" /> Every week
				</span>
			</span>
		);
	}
	const past = isPastDate(slot);
	return (
		<span className="slot-editor__day">
			<span className="ps-cell-strong">{formatSlotDate(slot)}</span>
			<span className="slot-editor__kind slot-editor__kind--date">
				<i className="fa-sharp fa-solid fa-calendar-day" />
				{kind === 'makeup' ? ' Makeup, this date only' : ' This date only'}
				{past && <span className="slot-editor__past"> (past)</span>}
			</span>
		</span>
	);
};

// A sortable column header.
//
// The clickable element is a real <button> rather than an onClick on the <th>
// (the Students.js precedent) so the column is reachable by keyboard at all;
// `aria-sort` on the th is what tells a screen reader which column is driving
// the order and which way.
//
// Defined at MODULE scope, not inside SlotEditorTab. A component declared in the
// render body gets a new function identity every render, so React unmounts and
// remounts it — which would throw focus off the header button on the very click
// that sorted it, breaking the keyboard path this markup exists to provide.
const SortableTh = ({ colKey, label, className = '', sort, onSort }) => {
	const active = sort.key === colKey;
	return (
		<th
			className={`${className} ps-sortable`.trim()}
			aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
			{/* `ps-sort-btn` rather than the page-local `slot-editor__sort` it used
			    to carry: the two were identical, and the shared class is what the
			    fit-to-width rules key off — under .ps-table--fit the label must be
			    its own element or a truncated header hard-clips mid-word. */}
			<button type="button" className="ps-sort-btn" title={label} onClick={() => onSort(colKey)}>
				<span className="ps-sort-btn__label">{label}</span>
				<i
					className={`fa-sharp fa-solid ${
						active
							? sort.direction === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'
							: 'fa-arrows-up-down ps-sort-btn__idle'
					}`}
					aria-hidden="true"
				/>
			</button>
		</th>
	);
};

// Ported from the legacy ManageTimeSlots page (CRUD on timeSlots), with the
// GenerateTestTimeSlots seed folded in as a guarded admin action. Write logic is
// unchanged; only the chrome moved from Web Awesome to the .cyv kit.
const SlotEditorTab = () => {
	const { userProfile, currentUser } = useAuth();
	const isAdmin = userProfile?.role === 'admin';

	const [timeSlots, setTimeSlots] = useState([]);
	const [loading, setLoading] = useState(true);
	const [permDenied, setPermDenied] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [editingSlot, setEditingSlot] = useState(null);
	// 'all' | 'open' | 'open_registration' | 'open_makeup' | 'assigned' | 'provisional' | 'blocked'
	const [filterStatus, setFilterStatus] = useState('all');
	const [filterDay, setFilterDay] = useState('all');

	// Sort is per-session on purpose: it is a way of LOOKING at the table right
	// now ("who is still awaiting an answer"), not a setting. Coming back
	// tomorrow to a table silently ordered by Duration would be a surprise, so
	// every load starts on day-then-time — the order this screen has always had.
	const [sort, setSort] = useState(DEFAULT_SLOT_SORT);
	const handleSort = (key) => setSort((prev) => nextSort(prev, key));

	// Columns, by contrast, ARE a setting, and stored per user in Firestore
	// rather than localStorage so the choice follows Anthony across laptop,
	// phone and the installed PWA. userPrefs/{uid} is namespaced by feature, so
	// `slotEditor` joins `dashboard` and `workBoard` in the existing document
	// under the existing rule — no rules change.
	const viewerUid = currentUser?.uid;
	const [columns, setColumns] = useState(() => normalizeSlotColumns(null));

	useEffect(() => {
		if (!viewerUid) return undefined;
		return subscribeToMyPrefs(viewerUid, (prefs) =>
			setColumns(normalizeSlotColumns(prefs?.slotEditor?.columns))
		);
	}, [viewerUid]);

	// Optimistic, matching the dashboard: local state moves first so a toggle
	// feels instant and the write follows. A failed write is swallowed on
	// purpose — the subscription above is the source of truth and corrects the
	// UI on the next tick, and a column preference is never worth an error
	// dialog on a screen that is also booking children into lesson times.
	const handleColumnsChange = (next) => {
		setColumns(next);
		if (viewerUid) {
			saveMyPrefs(viewerUid, 'slotEditor', { columns: next }).catch(() => {});
		}
	};

	// Form fields
	const [dayOfWeek, setDayOfWeek] = useState('');
	const [startTime, setStartTime] = useState('');
	const [duration, setDuration] = useState(45);
	const [instructor, setInstructor] = useState('');
	const [location, setLocation] = useState('Lakeville');
	const [notes, setNotes] = useState('');
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	// Bulk create (Track 1): N consecutive open slots for one day/location in a
	// single action — the fall-pool builder. Same doc shape as the single-slot
	// create path; the confirm shows the exact list before anything is written.
	const [showBulk, setShowBulk] = useState(false);
	const [bulk, setBulk] = useState({
		dayOfWeek: '',
		location: 'Lakeville',
		instructor: '',
		startTime: '',
		duration: 45,
		count: 4,
		gap: 0,
	});
	const [bulkSaving, setBulkSaving] = useState(false);
	const [bulkError, setBulkError] = useState('');

	// Bulk select → delete / block / unblock (the "wipe the board and rebuild"
	// pass). Selection is ids only; `assigned` slots are never selectable, so a
	// batch can't orphan a student's scheduled time — same guard as the per-row
	// lock. Delete stays admin-only (matches the per-row button + the rule).
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [bulkBusy, setBulkBusy] = useState(false);
	const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
	const [showBulkConfirmHolds, setShowBulkConfirmHolds] = useState(false);
	const [bulkActionError, setBulkActionError] = useState('');

	useEffect(() => {
		fetchTimeSlots();
	}, []);

	const fetchTimeSlots = async () => {
		setLoading(true);
		try {
			// Row order is no longer decided here — sortSlots owns it, and it
			// tiebreaks on day-then-time, so the default view is byte-identical
			// to the sort this function used to do. Leaving a second ordering
			// in place would just be a lie about which one wins.
			setTimeSlots(await fetchAllTimeSlots());
		} catch (error) {
			console.error('Error fetching time slots:', error);
			// A schedule-read denial must not read as "Create your first open
			// slot" (#27).
			if (isPermissionDenied(error)) setPermDenied(true);
		} finally {
			setLoading(false);
		}
	};

	const handleOpenModal = (slot = null) => {
		if (slot) {
			setEditingSlot(slot);
			setDayOfWeek(slot.dayOfWeek.toString());
			setStartTime(slot.startTime);
			setDuration(slot.duration);
			// Coerce null/absent instructor → '' so the controlled <select> stays
			// controlled (instructor is optional; slots rotate teachers per occurrence).
			setInstructor(slot.instructor || '');
			setLocation(slot.location);
			setNotes(slot.notes || '');
		} else {
			setEditingSlot(null);
			resetForm();
		}
		setShowModal(true);
		setError('');
	};

	const resetForm = () => {
		setDayOfWeek('');
		setStartTime('');
		setDuration(45);
		setInstructor('');
		setLocation('Lakeville');
		setNotes('');
	};

	const calculateEndTime = (start, durationMins) => {
		if (!start) return '';
		const [hours, minutes] = start.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes + durationMins;
		const endHours = Math.floor(totalMinutes / 60) % 24;
		const endMinutes = totalMinutes % 60;
		return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
	};

	const handleSave = async () => {
		// Instructor is optional: a timeSlot holds a student's recurring weekly time;
		// instructors rotate through it per occurrence (rotation lives in
		// scheduleAssignments). Only day + start time are actually required.
		if (!dayOfWeek || !startTime) {
			setError('Please fill in all required fields');
			return;
		}

		setSaving(true);
		setError('');

		try {
			const endTime = calculateEndTime(startTime, duration);

			const conflictData = {
				dayOfWeek: parseInt(dayOfWeek),
				startTime,
				endTime,
				instructor,
				location,
				isRecurring: true,
				excludeSlotId: editingSlot?.id
			};

			const conflicts = await checkTimeSlotConflicts(conflictData);

			if (conflicts.length > 0) {
				setError(`Conflict found: ${conflicts[0].reason}`);
				setSaving(false);
				return;
			}

			const timeSlotData = {
				dayOfWeek: parseInt(dayOfWeek),
				startTime,
				endTime,
				duration,
				location,
				// Store null (not '') for "no instructor" — keeps the data honest and
				// matches the field-absent shape of existing rotating slots.
				instructor: instructor || null,
				isRecurring: true,
				// Preserve the slot's existing status/makeup flag on edit — editing an
				// unrelated field must not silently unblock a blocked slot (#12).
				// New slots default to an open, non-makeup slot.
				isMakeup: editingSlot ? (editingSlot.isMakeup ?? false) : false,
				status: editingSlot ? (editingSlot.status || 'open') : 'open',
				notes: notes.trim(),
				updatedAt: Timestamp.now()
			};

			if (editingSlot) {
				await updateTimeSlot(editingSlot.id, timeSlotData);
			} else {
				timeSlotData.studentId = null;
				timeSlotData.studentName = null;
				timeSlotData.startDate = new Date().toISOString().split('T')[0];
				timeSlotData.endDate = null;
				timeSlotData.createdAt = Timestamp.now();

				await addTimeSlot(timeSlotData);
			}

			setShowModal(false);
			fetchTimeSlots();
		} catch (err) {
			console.error('Error saving time slot:', err);
			setError('Error saving time slot. Please try again.');
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (slot) => {
		if (!window.confirm(
			`Are you sure you want to delete this time slot?\n\n${formatSlotDate(slot) || `${getDayName(slot.dayOfWeek)}s`} at ${formatTime12Hour(slot.startTime)}\n\nThis cannot be undone.`
		)) {
			return;
		}

		try {
			await deleteTimeSlot(slot.id);
			fetchTimeSlots();
		} catch (error) {
			console.error('Error deleting time slot:', error);
			alert('Error deleting time slot. Please try again.');
		}
	};

	const handleToggleStatus = async (slot) => {
		try {
			const newStatus = slot.status === 'open' ? 'blocked' : 'open';
			await updateTimeSlot(slot.id, {
				status: newStatus,
				updatedAt: Timestamp.now()
			});
			fetchTimeSlots();
		} catch (error) {
			console.error('Error updating slot status:', error);
			alert('Error updating status. Please try again.');
		}
	};

	// Admin escape hatch: return an assigned slot to "open" (the same write the
	// student-profile "Unassign" issues — studentId/name null, status open).
	// Assigned rows are otherwise locked here so a stray edit/delete can't orphan
	// a student's scheduled time, but that lock leaves NO way to free a slot when
	// the student is unreachable — e.g. already archived, which strands the slot
	// with no profile to reach the profile-side Unassign. This gives admins that
	// path. Non-destructive and reversible (reassign any time), so a plain confirm
	// matches the per-row delete pattern rather than the bulk-delete modal.
	const handleUnassign = async (slot) => {
		const slotDescription = slot.isRecurring
			? `${getDayName(slot.dayOfWeek)}s at ${formatTime12Hour(slot.startTime)} (${slot.location})`
			: `${slot.specificDate || ''} at ${formatTime12Hour(slot.startTime)} (${slot.location})`;

		if (!window.confirm(
			`Unassign ${slot.studentName || 'this student'} from this slot?\n\n${slotDescription}\n\nThe slot returns to "open" and becomes available again. You can reassign it any time.`
		)) {
			return;
		}

		try {
			await updateTimeSlot(slot.id, unassignSlotPatch());
			fetchTimeSlots();
		} catch (error) {
			console.error('Error unassigning time slot:', error);
			alert('Error unassigning slot. Please try again.');
		}
	};

	// ---- provisional carryover holds ---------------------------------------
	//
	// A hold is last year's student pencilled into this year's grid while Grace
	// phones each family. Confirming turns it into a real booking; releasing
	// returns the time to the public /register pool. Both are single, reversible
	// writes, so they follow handleUnassign's plain-confirm pattern rather than
	// the bulk-delete modal.

	const holdDescription = (slot) =>
		`${getDayName(slot.dayOfWeek)}s at ${formatTime12Hour(slot.startTime)} (${slot.location})`;

	const handleConfirmHold = async (slot) => {
		if (!window.confirm(
			`${slot.holdStudentName || 'This student'} is keeping this time?\n\n${holdDescription(slot)}\n\nThe slot becomes a confirmed lesson and shows on the calendar and in their family's portal.`
		)) return;
		try {
			await updateTimeSlot(slot.id, confirmHoldPatch(slot, currentUser?.uid));
			fetchTimeSlots();
		} catch (error) {
			console.error('Error confirming hold:', error);
			alert('Error confirming this time. Please try again.');
		}
	};

	const handleReleaseHold = async (slot) => {
		if (!window.confirm(
			`Release ${slot.holdStudentName || 'this student'}'s time?\n\n${holdDescription(slot)}\n\nThe slot returns to the open pool and new families can request it. You can place someone here again any time.`
		)) return;
		try {
			await updateTimeSlot(slot.id, releaseHoldPatch());
			fetchTimeSlots();
		} catch (error) {
			console.error('Error releasing hold:', error);
			alert('Error releasing this slot. Please try again.');
		}
	};

	// Bulk confirm is a bulk ASSIGNMENT — it books real children into real times
	// — so it gets the preview-then-confirm modal listing every name, not a bare
	// window.confirm. Release is reversible and gets the lighter treatment.
	const handleBulkHold = async (mode) => {
		const chosen = timeSlots.filter(s => selectedIds.has(s.id) && s.status === 'provisional');
		if (chosen.length === 0) return;
		setBulkBusy(true);
		setBulkActionError('');
		try {
			if (mode === 'confirm') await confirmProvisionalHolds(chosen, currentUser?.uid);
			else await releaseProvisionalHolds(chosen.map(s => s.id));
			setSelectedIds(new Set());
			setShowBulkConfirmHolds(false);
			fetchTimeSlots();
		} catch (err) {
			console.error('Error updating holds:', err);
			setBulkActionError(`Error ${mode === 'confirm' ? 'confirming' : 'releasing'} holds — some may not have changed. Refresh and check.`);
		} finally {
			setBulkBusy(false);
		}
	};

	// Preview of the bulk run: `count` back-to-back slots from startTime, each
	// duration + gap minutes apart. Pure derivation — nothing written until
	// the user confirms.
	const bulkPreview = (() => {
		if (!bulk.dayOfWeek || !bulk.startTime) return [];
		const [h, m] = bulk.startTime.split(':').map(Number);
		const out = [];
		let startMins = h * 60 + m;
		for (let i = 0; i < Math.max(1, Math.min(20, Number(bulk.count) || 1)); i++) {
			const sh = Math.floor(startMins / 60) % 24;
			const sm = startMins % 60;
			const start = `${String(sh).padStart(2, '0')}:${String(sm).padStart(2, '0')}`;
			out.push({ startTime: start, endTime: calculateEndTime(start, bulk.duration) });
			startMins += Number(bulk.duration) + (Number(bulk.gap) || 0);
		}
		return out;
	})();

	const handleBulkCreate = async () => {
		if (!bulk.dayOfWeek || !bulk.startTime) {
			setBulkError('Pick a day and a first start time.');
			return;
		}
		setBulkSaving(true);
		setBulkError('');
		try {
			for (const s of bulkPreview) {
				await addTimeSlot({
					studentId: null,
					studentName: null,
					dayOfWeek: parseInt(bulk.dayOfWeek),
					startTime: s.startTime,
					endTime: s.endTime,
					duration: Number(bulk.duration),
					location: bulk.location,
					instructor: bulk.instructor || null,
					isRecurring: true,
					isMakeup: false,
					status: 'open',
					startDate: new Date().toISOString().split('T')[0],
					endDate: null,
					notes: '',
					createdAt: Timestamp.now(),
					updatedAt: Timestamp.now(),
				});
			}
			setShowBulk(false);
			fetchTimeSlots();
		} catch (err) {
			console.error('Error bulk-creating time slots:', err);
			setBulkError('Error creating slots — some may have been created. Refresh and check.');
		} finally {
			setBulkSaving(false);
		}
	};

	const filteredSlots = timeSlots.filter(slot => {
		if (filterStatus !== 'all') {
			if (filterStatus === 'open' && slot.status !== 'open') return false;
			if (filterStatus === 'open_registration' && !isOpenForRegistration(slot)) return false;
			if (filterStatus === 'open_makeup' && !isOpenForMakeup(slot)) return false;
			if (filterStatus === 'blocked' && slot.status !== 'blocked') return false;
			if (filterStatus === 'assigned' && slot.status !== 'assigned') return false;
			if (filterStatus === 'provisional' && slot.status !== 'provisional') return false;
		}

		if (filterDay !== 'all') {
			// slotWeekday, not dayOfWeek: a makeup doc has only a date, so
			// filtering to Friday used to hide every Friday makeup.
			if (slotWeekday(slot) !== parseInt(filterDay)) return false;
		}

		return true;
	});

	// Sort is presentation only: it reorders the same set the filters already
	// chose, so nothing below (selection, counts, bulk actions) changes meaning.
	const visibleSlots = sortSlots(filteredSlots, sort);

	// Only non-assigned rows currently visible under the active filters can be
	// selected — so "filter to Tuesday, select all, delete" can never reach a
	// hidden row or an assigned one.
	const selectableSlots = visibleSlots.filter(slot => slot.status !== 'assigned');
	// Sorted so the key identifies the SET, not the order. Without it a column
	// click rewrites the key and re-runs the prune effect for no reason.
	const selectableKey = selectableSlots.map(s => s.id).sort().join(',');

	// Prune the selection whenever the visible selectable set changes (filter
	// change, refetch) so the "N selected" count always matches what's on screen
	// and an action can't touch a row the user can no longer see. Returning the
	// previous Set unchanged keeps this from looping.
	useEffect(() => {
		const allowed = new Set(selectableKey ? selectableKey.split(',') : []);
		setSelectedIds(prev => {
			if (prev.size === 0) return prev;
			const next = new Set([...prev].filter(id => allowed.has(id)));
			return next.size === prev.size ? prev : next;
		});
	}, [selectableKey]);

	const allSelectableSelected = selectableSlots.length > 0 && selectedIds.size === selectableSlots.length;
	const someSelectableSelected = selectedIds.size > 0 && !allSelectableSelected;

	// A provisional row stays selectable — the same checkboxes drive bulk confirm
	// and release — but Delete disappears the moment one is in the selection. A
	// hold is a child's time; deleting it loses the fact that they had it, which
	// is precisely what nobody can reconstruct once Grace's spreadsheet moves on.
	const selectedSlots = timeSlots.filter(s => selectedIds.has(s.id));
	const selectedHolds = selectedSlots.filter(s => s.status === 'provisional');
	const selectionHasHold = selectedHolds.length > 0;
	const selectionAllHolds = selectionHasHold && selectedHolds.length === selectedSlots.length;

	const toggleSelect = (id) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id); else next.add(id);
			return next;
		});
	};

	const toggleSelectAll = () => {
		setSelectedIds(prev => (
			prev.size === selectableSlots.length ? new Set() : new Set(selectableSlots.map(s => s.id))
		));
	};

	// Breakdown for the delete confirm — the user sees the exact count and what
	// they're made of before anything is written.
	const selectedBreakdown = (() => {
		const chosen = selectableSlots.filter(s => selectedIds.has(s.id));
		return {
			total: chosen.length,
			open: chosen.filter(s => s.status === 'open').length,
			blocked: chosen.filter(s => s.status === 'blocked').length,
		};
	})();

	const runBulkDelete = async () => {
		setShowBulkDeleteConfirm(false);
		setBulkBusy(true);
		setBulkActionError('');
		try {
			await deleteTimeSlots([...selectedIds]);
			setSelectedIds(new Set());
			fetchTimeSlots();
		} catch (err) {
			console.error('Error bulk-deleting time slots:', err);
			// Chunks commit independently — an error partway through leaves the
			// earlier chunks deleted. Say so rather than implying a clean rollback.
			setBulkActionError('Error deleting slots — some may not have been deleted. Refresh and check.');
		} finally {
			setBulkBusy(false);
		}
	};

	const handleBulkStatus = async (status) => {
		setBulkBusy(true);
		setBulkActionError('');
		try {
			await updateTimeSlotsStatus([...selectedIds], status);
			setSelectedIds(new Set());
			fetchTimeSlots();
		} catch (err) {
			console.error('Error bulk-updating slot status:', err);
			setBulkActionError(`Error ${status === 'blocked' ? 'blocking' : 'unblocking'} slots — some may not have changed. Refresh and check.`);
		} finally {
			setBulkBusy(false);
		}
	};

	const statusCounts = {
		open: timeSlots.filter(s => s.status === 'open').length,
		openRegistration: timeSlots.filter(isOpenForRegistration).length,
		openMakeup: timeSlots.filter(isOpenForMakeup).length,
		assigned: timeSlots.filter(s => s.status === 'assigned').length,
		provisional: timeSlots.filter(s => s.status === 'provisional').length,
		blocked: timeSlots.filter(s => s.status === 'blocked').length
	};

	const statusTone = (status) => {
		switch (status) {
			case 'open': return 'success';
			case 'assigned': return 'info';
			// Warning, not neutral: a hold is an open question, and the whole
			// point of this screen during carryover is seeing how many are left.
			case 'provisional': return 'warning';
			default: return 'neutral';
		}
	};

	return (
		<div className="slot-editor">
			<div className="slot-editor__head">
				<h2 className="ps-card__title">
					<i className="fa-sharp fa-solid fa-calendar-pen" /> Manage Time Slots
				</h2>
				<div className="slot-editor__head-actions">
					<button
						className="ps-btn"
						onClick={() => { setBulkError(''); setShowBulk(true); }}
						title="Create several consecutive open slots for one day/location">
						<i className="fa-sharp fa-solid fa-layer-group" /> Bulk create
					</button>
					<button className="btn-cyv-teal slot-editor__create-btn" onClick={() => handleOpenModal()}>
						<i className="fa-sharp fa-solid fa-plus" /> Create Open Slot
					</button>
				</div>
			</div>

			{/* Stats */}
			<div className="slot-editor__stats">
				{/* Two cards, never one "Open" total: the two kinds of open are
				    different things (see slotKind.js). */}
				<div className="ps-stat" style={{ borderLeft: '4px solid var(--cyv-teal-600)' }}>
					<p className="ps-stat__num">{statusCounts.openRegistration}</p>
					<span className="ps-stat__label">Open for registration</span>
				</div>
				<div className="ps-stat" style={{ borderLeft: '4px solid var(--cyv-teal-600)' }}>
					<p className="ps-stat__num">{statusCounts.openMakeup}</p>
					<span className="ps-stat__label">Open for makeups</span>
				</div>
				<div className="ps-stat" style={{ borderLeft: '4px solid var(--cyv-teal-500)' }}>
					<p className="ps-stat__num">{statusCounts.assigned}</p>
					<span className="ps-stat__label">Assigned Slots</span>
				</div>
				{/* Only while carryover is in flight — once every family has
				    answered this reads 0 and is just noise on the screen. */}
				{statusCounts.provisional > 0 && (
					<div className="ps-stat" style={{ borderLeft: '4px solid var(--wa-color-warning-60, #d98324)' }}>
						<p className="ps-stat__num">{statusCounts.provisional}</p>
						<span className="ps-stat__label">Awaiting an answer</span>
					</div>
				)}
				<div className="ps-stat" style={{ borderLeft: '4px solid var(--cyv-muted)' }}>
					<p className="ps-stat__num">{statusCounts.blocked}</p>
					<span className="ps-stat__label">Blocked Slots</span>
				</div>
			</div>

			{/* Filters */}
			<div className="ps-toolbar">
				<select className="ps-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
					<option value="all">All Statuses</option>
					<option value="open_registration">Open for registration (weekly)</option>
					<option value="open_makeup">Open for makeups (one date)</option>
					<option value="open">All open</option>
					<option value="assigned">Assigned Only</option>
					<option value="provisional">Awaiting an answer</option>
					<option value="blocked">Blocked</option>
				</select>
				<select className="ps-select" value={filterDay} onChange={(e) => setFilterDay(e.target.value)}>
					<option value="all">All Days</option>
					{DAYS_OF_WEEK.map(day => (
						<option key={day.value} value={day.value}>{day.label}</option>
					))}
				</select>
				{/* Desktop-only affordance — hidden below the stack breakpoint,
				    where every row is already a card showing every field. */}
				<PropertyPicker
					catalog={SLOT_COLUMNS}
					defaults={DEFAULT_SLOT_COLUMNS}
					selected={columns}
					onChange={handleColumnsChange}
					title="Show columns"
					triggerLabel="Columns"
					triggerIcon="fa-table-columns"
					triggerClassName="ps-btn"
					note="Day, time and actions always show. On a phone every field shows regardless."
				/>
			</div>

			{bulkActionError && <div className="ps-alert ps-alert--danger">{bulkActionError}</div>}

			{/* Bulk action bar — only once something is selected. Delete is
			    admin-only (matches the per-row button + the rule); block/unblock
			    is available to any staff who can already use the per-row toggle. */}
			{selectedIds.size > 0 && (
				<div className="slot-editor__bulkbar">
					<span className="slot-editor__bulkbar-count">
						<i className="fa-sharp fa-solid fa-check-double" /> {selectedIds.size} selected
					</span>
					<div className="slot-editor__bulkbar-actions">
						{/* Hold actions only when every selected row is a hold —
						    a mixed selection makes "confirm" meaningless. */}
						{isAdmin && selectionAllHolds && (
							<>
								<button
									className="ps-btn"
									onClick={() => setShowBulkConfirmHolds(true)}
									disabled={bulkBusy}>
									<i className="fa-sharp fa-solid fa-check" /> They&rsquo;re keeping these
								</button>
								<button
									className="ps-btn"
									onClick={() => handleBulkHold('release')}
									disabled={bulkBusy}>
									<i className="fa-sharp fa-solid fa-user-slash" /> Release these
								</button>
							</>
						)}
						{/* Block/unblock write `status` and nothing else, so running
						    one over a hold would overwrite 'provisional' and strand
						    holdStudentId — the slot would still name a child while
						    claiming to be blocked. Hidden rather than filtered, so
						    the count in the bar never lies about what will change. */}
						{!selectionHasHold && (
							<>
								<button
									className="ps-btn"
									onClick={() => handleBulkStatus('blocked')}
									disabled={bulkBusy}>
									<i className="fa-sharp fa-solid fa-ban" /> Block selected
								</button>
								<button
									className="ps-btn"
									onClick={() => handleBulkStatus('open')}
									disabled={bulkBusy}>
									<i className="fa-sharp fa-solid fa-unlock" /> Unblock selected
								</button>
							</>
						)}
						{selectionHasHold && !selectionAllHolds && (
							<span className="text-muted">
								<i className="fa-sharp fa-solid fa-circle-info me-1" />
								Mixed selection — filter to &ldquo;Awaiting an answer&rdquo; to act on holds.
							</span>
						)}
						{/* Delete hides whenever a hold is selected — see the
						    selectionHasHold note above. */}
						{isAdmin && !selectionHasHold && (
							<button
								className="ps-btn slot-editor__bulkbar-delete"
								onClick={() => setShowBulkDeleteConfirm(true)}
								disabled={bulkBusy}>
								<i className="fa-sharp fa-solid fa-trash" /> Delete selected
							</button>
						)}
						<button
							className="btn-cyv-ghost"
							onClick={() => setSelectedIds(new Set())}
							disabled={bulkBusy}>
							Clear
						</button>
						{bulkBusy && <span className="ps-spinner slot-editor__btn-spin" />}
					</div>
				</div>
			)}

			{/* Table */}
			{loading ? (
				<div className="ps-spinner" role="status" aria-label="Loading" />
			) : permDenied ? (
				<AccessNotice what="the schedule" />
			) : visibleSlots.length === 0 ? (
				<div className="ps-alert ps-alert--info">
					<i className="fa-sharp fa-solid fa-circle-info me-2" />
					No time slots found. Create your first open slot to get started!
				</div>
			) : (
				<div className="ps-table-wrap">
					<table className={`ps-table ps-table--fit ${hiddenColumnClasses(columns)}`.trim()}>
						<thead>
							<tr>
								<th className="slot-editor__select-col col--select">
									<input
										type="checkbox"
										aria-label="Select all visible slots"
										title="Select all visible, unassigned slots"
										checked={allSelectableSelected}
										ref={(el) => { if (el) el.indeterminate = someSelectableSelected; }}
										disabled={selectableSlots.length === 0 || bulkBusy}
										onChange={toggleSelectAll}
									/>
								</th>
								<SortableTh colKey="status" label="Status" className="col--status" sort={sort} onSort={handleSort} />
								<SortableTh colKey="day" label="Day" className="col--day" sort={sort} onSort={handleSort} />
								<SortableTh colKey="time" label="Time" className="col--time" sort={sort} onSort={handleSort} />
								<SortableTh colKey="duration" label="Duration" className="col--duration" sort={sort} onSort={handleSort} />
								<SortableTh colKey="instructor" label="Instructor" className="col--instructor" sort={sort} onSort={handleSort} />
								<SortableTh colKey="location" label="Location" className="col--location" sort={sort} onSort={handleSort} />
								<SortableTh colKey="student" label="Student" className="col--student" sort={sort} onSort={handleSort} />
								<th className="col--actions">Actions</th>
							</tr>
						</thead>
						<tbody>
							{visibleSlots.map(slot => (
								<tr key={slot.id} className={selectedIds.has(slot.id) ? 'slot-editor__row--selected' : undefined}>
									<td data-label="Select" className="slot-editor__select-col col--select">
										{/* Assigned slots get no checkbox — deleting one would orphan a
										    student's scheduled time (same guard as the per-row lock). */}
										{slot.status !== 'assigned' ? (
											<input
												type="checkbox"
												aria-label={`Select ${formatSlotDate(slot) || getDayName(slot.dayOfWeek)} ${formatTime12Hour(slot.startTime)} slot`}
												checked={selectedIds.has(slot.id)}
												disabled={bulkBusy}
												onChange={() => toggleSelect(slot.id)}
											/>
										) : (
											<span className="text-muted"><i className="fa-sharp fa-solid fa-lock" /></span>
										)}
									</td>
									<td data-label="Status" className="col--status"><span className={`ps-badge ps-badge--${statusTone(slot.status)}`}>{slot.status}</span></td>
									<td data-label="Day" className="col--day"><SlotDayCell slot={slot} /></td>
									<td data-label="Time" className="col--time"><span className="ps-clip">{formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}</span></td>
									<td data-label="Duration" className="col--duration">{slot.duration} min</td>
									<td data-label="Instructor" className="col--instructor">{slot.instructor
										? <span className="ps-badge ps-badge--info">{slot.instructor}</span>
										: <span className="text-muted">—</span>}</td>
									<td data-label="Location" className="col--location"><span className="ps-badge ps-badge--neutral">{slot.location}</span></td>
									<td data-label="Student" className="col--student">
										<span className="ps-clip" title={slot.studentName || slot.holdStudentName || ''}>
											{slot.studentName
												|| (slot.holdStudentName
													? <span title="Held from last year — not confirmed yet">{slot.holdStudentName}</span>
													: <span className="text-muted">—</span>)}
										</span>
									</td>
									<td data-label="Actions" className="col--actions">
										{slot.status === 'provisional' ? (
											isAdmin ? (
												<div className="d-flex gap-1">
													<button
														className="ps-btn-icon"
														onClick={() => handleConfirmHold(slot)}
														title={`${slot.holdStudentName || 'This student'} is keeping this time`}>
														<i className="fa-sharp fa-solid fa-check" />
													</button>
													<button
														className="ps-btn-icon"
														onClick={() => handleReleaseHold(slot)}
														title="Release this time back to the open pool">
														<i className="fa-sharp fa-solid fa-user-slash" />
													</button>
												</div>
											) : (
												<span className="text-muted">
													<i className="fa-sharp fa-solid fa-lock me-1" /> Held
												</span>
											)
										) : slot.status !== 'assigned' ? (
											<div className="d-flex gap-1">
												<button className="ps-btn-icon" onClick={() => handleOpenModal(slot)} title="Edit">
													<i className="fa-sharp fa-solid fa-pen" />
												</button>
												<button
													className="ps-btn-icon"
													onClick={() => handleToggleStatus(slot)}
													title={slot.status === 'open' ? 'Block this slot' : 'Unblock this slot'}>
													<i className={`fa-sharp fa-solid fa-${slot.status === 'open' ? 'ban' : 'unlock'}`} />
												</button>
												{/* timeSlots delete is admin-only (firestore.rules) — hide
												    for teachers rather than fail at the server (#25). */}
												{isAdmin && (
													<button className="ps-btn-icon" onClick={() => handleDelete(slot)} title="Delete">
														<i className="fa-sharp fa-solid fa-trash" />
													</button>
												)}
											</div>
										) : isAdmin ? (
											// Admin-only: free an assigned slot in place (no need to
											// reach the — possibly archived — student's profile).
											<button
												className="ps-btn-icon"
												onClick={() => handleUnassign(slot)}
												title={`Unassign ${slot.studentName || 'student'} — returns the slot to open`}>
												<i className="fa-sharp fa-solid fa-user-slash" />
											</button>
										) : (
											<span className="text-muted">
												<i className="fa-sharp fa-solid fa-lock me-1" /> Assigned
											</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{/* Create/Edit Modal */}
			{showModal && (
				<div className="ps-modal-overlay" onClick={() => setShowModal(false)}>
					<div className="ps-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
						<h3 className="ps-modal__title">{editingSlot ? 'Edit Time Slot' : 'Create Open Time Slot'}</h3>

						{error && <div className="ps-alert ps-alert--danger">{error}</div>}

						<div className="slot-editor__form-row">
							<div className="ps-field">
								<label className="ps-label">Day of Week <span className="ps-req">*</span></label>
								<select className="ps-select" value={dayOfWeek} onChange={(e) => setDayOfWeek(e.target.value)}>
									<option value="">Select day…</option>
									{DAYS_OF_WEEK.map(day => (
										<option key={day.value} value={day.value}>{day.label}</option>
									))}
								</select>
							</div>
							<div className="ps-field">
								<label className="ps-label">Start Time <span className="ps-req">*</span></label>
								<input className="ps-input" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
							</div>
						</div>

						<div className="slot-editor__form-row slot-editor__form-row--3">
							<div className="ps-field">
								<label className="ps-label">Duration <span className="ps-req">*</span></label>
								<select className="ps-select" value={duration} onChange={(e) => setDuration(parseInt(e.target.value))}>
									<option value={30}>30 minutes</option>
									<option value={45}>45 minutes</option>
									<option value={60}>60 minutes</option>
									<option value={90}>90 minutes</option>
									{/* 105 exists because the Triple Threat block runs 6:30-8:15.
									    Without it, opening that slot shows a mismatched select and
									    saving silently rewrites its length. */}
									<option value={105}>105 minutes</option>
								</select>
								{startTime && (
									<small className="text-muted">End time: {calculateEndTime(startTime, duration)}</small>
								)}
							</div>
							<div className="ps-field">
								<label className="ps-label">Instructor <span className="text-muted">(optional)</span></label>
								<select className="ps-select" value={instructor} onChange={(e) => setInstructor(e.target.value)}>
									<option value="">No instructor (rotates)</option>
									{INSTRUCTORS.map(inst => (<option key={inst} value={inst}>{inst}</option>))}
								</select>
							</div>
							<div className="ps-field">
								<label className="ps-label">Location <span className="ps-req">*</span></label>
								<select className="ps-select" value={location} onChange={(e) => setLocation(e.target.value)}>
									{LOCATIONS.map(loc => (<option key={loc} value={loc}>{loc}</option>))}
								</select>
							</div>
						</div>

						<div className="ps-field slot-editor__form-row-single">
							<label className="ps-label">Notes (Optional)</label>
							<textarea className="ps-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any notes about this time slot…" />
						</div>

						<div className="ps-modal__footer">
							<button className="btn-cyv-ghost" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
							<button className="btn-cyv-teal" onClick={handleSave} disabled={saving}>
								{saving ? (
									<><span className="ps-spinner slot-editor__btn-spin" /> Saving…</>
								) : (
									<><i className="fa-sharp fa-solid fa-check" /> {editingSlot ? 'Update' : 'Create'} Time Slot</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Bulk create open slots (fall-pool builder) */}
			{showBulk && (
				<div className="ps-modal-overlay" onClick={() => !bulkSaving && setShowBulk(false)}>
					<div className="ps-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
						<h3 className="ps-modal__title">Bulk create open slots</h3>

						{bulkError && <div className="ps-alert ps-alert--danger">{bulkError}</div>}

						<div className="slot-editor__form-row">
							<div className="ps-field">
								<label className="ps-label">Day of Week <span className="ps-req">*</span></label>
								<select
									className="ps-select"
									value={bulk.dayOfWeek}
									onChange={(e) => setBulk((b) => ({ ...b, dayOfWeek: e.target.value }))}>
									<option value="">Select day…</option>
									{DAYS_OF_WEEK.map(day => (
										<option key={day.value} value={day.value}>{day.label}</option>
									))}
								</select>
							</div>
							<div className="ps-field">
								<label className="ps-label">First start time <span className="ps-req">*</span></label>
								<input
									className="ps-input"
									type="time"
									value={bulk.startTime}
									onChange={(e) => setBulk((b) => ({ ...b, startTime: e.target.value }))}
								/>
							</div>
						</div>

						<div className="slot-editor__form-row slot-editor__form-row--3">
							<div className="ps-field">
								<label className="ps-label">Duration</label>
								<select
									className="ps-select"
									value={bulk.duration}
									onChange={(e) => setBulk((b) => ({ ...b, duration: parseInt(e.target.value) }))}>
									<option value={30}>30 minutes</option>
									<option value={45}>45 minutes</option>
									<option value={60}>60 minutes</option>
								</select>
							</div>
							<div className="ps-field">
								<label className="ps-label">How many slots</label>
								<input
									className="ps-input"
									type="number"
									min="1"
									max="20"
									value={bulk.count}
									onChange={(e) => setBulk((b) => ({ ...b, count: e.target.value }))}
								/>
							</div>
							<div className="ps-field">
								<label className="ps-label">Gap between (min)</label>
								<input
									className="ps-input"
									type="number"
									min="0"
									max="60"
									step="5"
									value={bulk.gap}
									onChange={(e) => setBulk((b) => ({ ...b, gap: e.target.value }))}
								/>
							</div>
						</div>

						<div className="slot-editor__form-row">
							<div className="ps-field">
								<label className="ps-label">Location <span className="ps-req">*</span></label>
								<select
									className="ps-select"
									value={bulk.location}
									onChange={(e) => setBulk((b) => ({ ...b, location: e.target.value }))}>
									{LOCATIONS.map(loc => (<option key={loc} value={loc}>{loc}</option>))}
								</select>
							</div>
							<div className="ps-field">
								<label className="ps-label">Instructor <span className="text-muted">(optional)</span></label>
								<select
									className="ps-select"
									value={bulk.instructor}
									onChange={(e) => setBulk((b) => ({ ...b, instructor: e.target.value }))}>
									<option value="">No instructor (rotates)</option>
									{INSTRUCTORS.map(inst => (<option key={inst} value={inst}>{inst}</option>))}
								</select>
							</div>
						</div>

						{bulkPreview.length > 0 && (
							<div className="ps-alert ps-alert--info">
								<i className="fa-sharp fa-solid fa-eye me-2" />
								Will create <strong>{bulkPreview.length} open {DAY_NAMES[parseInt(bulk.dayOfWeek)]} slots</strong> at {bulk.location}:{' '}
								{bulkPreview.map((s) => formatTime12Hour(s.startTime)).join(', ')}
							</div>
						)}

						<div className="ps-modal__footer">
							<button className="btn-cyv-ghost" onClick={() => setShowBulk(false)} disabled={bulkSaving}>Cancel</button>
							<button
								className="btn-cyv-teal"
								onClick={handleBulkCreate}
								disabled={bulkSaving || bulkPreview.length === 0}>
								{bulkSaving ? (
									<><span className="ps-spinner slot-editor__btn-spin" /> Creating…</>
								) : (
									<><i className="fa-sharp fa-solid fa-layer-group" /> Create {bulkPreview.length || ''} slots</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Bulk delete confirm — names the exact count + breakdown before any
			    write. Destructive and irreversible, so never a bare window.confirm. */}
			{showBulkDeleteConfirm && (
				<div className="ps-modal-overlay" onClick={() => setShowBulkDeleteConfirm(false)}>
					<div className="ps-modal" onClick={(e) => e.stopPropagation()}>
						<h3 className="ps-modal__title">Delete {selectedBreakdown.total} time slots?</h3>
						<div className="ps-alert ps-alert--warning">
							<i className="fa-sharp fa-solid fa-triangle-exclamation me-2" />
							This permanently deletes <strong>{selectedBreakdown.total} time slot{selectedBreakdown.total === 1 ? '' : 's'}</strong>
							{' '}({selectedBreakdown.open} open, {selectedBreakdown.blocked} blocked) from the live <code>timeSlots</code> collection. <strong>This cannot be undone.</strong>
						</div>
						<p className="text-muted">
							Assigned slots are never included — only the unassigned rows visible under your current filters.
						</p>
						<div className="ps-modal__footer">
							<button className="btn-cyv-ghost" onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</button>
							<button className="ps-btn slot-editor__bulkbar-delete" onClick={runBulkDelete}>
								<i className="fa-sharp fa-solid fa-trash" /> Delete {selectedBreakdown.total} slots
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Bulk confirm holds. This books real children into real times, so it
			    lists every name rather than asking "confirm 12 holds?" — the
			    reviewable unit is who, not how many. Release is reversible and
			    stays on the lighter path. */}
			{showBulkConfirmHolds && (
				<div className="ps-modal-overlay" onClick={() => setShowBulkConfirmHolds(false)}>
					<div className="ps-modal" onClick={(e) => e.stopPropagation()}>
						<h3 className="ps-modal__title">Confirm {selectedHolds.length} time{selectedHolds.length === 1 ? '' : 's'}?</h3>
						<p className="text-muted">
							These become confirmed lessons and appear on the calendar and in each family&rsquo;s portal.
						</p>
						<ul className="slot-editor__gen-list">
							{selectedHolds
								.slice()
								.sort((a, b) => (a.dayOfWeek - b.dayOfWeek) || String(a.startTime).localeCompare(String(b.startTime)))
								.map(s => (
									<li key={s.id}>
										<strong>{s.holdStudentName || 'Unnamed'}</strong>
										{' — '}{getDayName(s.dayOfWeek)}s at {formatTime12Hour(s.startTime)} ({s.location})
									</li>
								))}
						</ul>
						<div className="ps-modal__footer">
							<button className="btn-cyv-ghost" onClick={() => setShowBulkConfirmHolds(false)}>Cancel</button>
							<button className="ps-btn" onClick={() => handleBulkHold('confirm')} disabled={bulkBusy}>
								<i className="fa-sharp fa-solid fa-check" /> Confirm {selectedHolds.length}
							</button>
						</div>
					</div>
				</div>
			)}

		</div>
	);
};

export default SlotEditorTab;
